import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet, Coins, CalendarClock, Gift, LayoutGrid, CheckCircle2, XCircle, Loader2, FlaskConical } from "lucide-react";
import { useLang } from "@/i18n";
import { API, SITE, shortAddress } from "@/config/site";
import { PageHero, Reveal, GoldParticles } from "@/components/common";
import { ConnectWalletButton } from "@/components/ConnectWalletButton";
import { useWallet } from "@/wallet/WalletContext";

const STEPS = [
  { icon: Wallet, tKey: "rewards.s1", dKey: "rewards.s1d" },
  { icon: Coins, tKey: "rewards.s2", dKey: "rewards.s2d" },
  { icon: CalendarClock, tKey: "rewards.s3", dKey: "rewards.s3d" },
  { icon: Gift, tKey: "rewards.s4", dKey: "rewards.s4d" },
  { icon: LayoutGrid, tKey: "rewards.s5", dKey: "rewards.s5d" },
];

export default function Mint() {
  const { t } = useLang();
  const { connected, address } = useWallet();
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (!address) return;
    setChecking(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/eligibility`, { address });
      setResult(r.data);
    } catch {
      setResult({ status: "TEMPORARILY_UNAVAILABLE", checks: [], demo: true });
    } finally {
      setChecking(false);
    }
  };

  const currentStep = !connected ? 0 : !result ? 1 : 3;

  return (
    <>
      <PageHero eyebrow={t("rewards.eyebrow")} title={t("mint.title")} sub={t("rewards.rule")} />

      <section className="section-pad relative" data-testid="mint-flow">
        <GoldParticles count={10} />
        <div className="grid gap-4 md:grid-cols-5">
          {STEPS.map((s, i) => {
            const done = i < currentStep;
            const active = i === currentStep;
            return (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  className={`card-surface relative h-full p-5 ${active ? "!border-neon/50" : ""} ${done ? "!border-brgreen/40" : ""}`}
                  data-testid={`mint-step-${i + 1}`}
                >
                  <div className="flex items-center justify-between">
                    <s.icon size={20} className={active ? "text-neon" : done ? "text-brgreen" : "text-mist"} />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-mist">0{i + 1}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-sm font-bold uppercase tracking-wide text-paper">{t(s.tKey)}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-mist">{t(s.dKey)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-8">
          <div className="card-surface relative overflow-hidden p-8" data-testid="eligibility-panel">
            <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gold/10 blur-3xl" />
            <div className="mb-6 flex items-center gap-2">
              <FlaskConical size={16} className="text-gold" />
              <span className="mono-chip border-gold/40 text-goldlight">{t("common.demo")}</span>
              <p className="text-xs text-mist">{t("mint.demoNote")}</p>
            </div>

            <AnimatePresence mode="wait">
              {!connected ? (
                <motion.div key="nc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-start gap-5">
                  <p className="font-heading text-xl font-bold uppercase tracking-wide text-paper" data-testid="mint-state">
                    {t("mint.state.wallet")}
                  </p>
                  <ConnectWalletButton />
                </motion.div>
              ) : (
                <motion.div key="c" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="mono-chip border-neon/40 text-neon" data-testid="mint-wallet">{shortAddress(address)}</span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{t("mint.balance")}</p>
                      <p className="font-mono text-sm font-bold text-goldlight">{t("mint.unavailable")}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button type="button" onClick={check} disabled={checking} className="btn-neon disabled:opacity-50" data-testid="check-eligibility-btn">
                      {checking ? <Loader2 size={15} className="animate-spin" /> : null}
                      {checking ? t("mint.checking") : t("mint.check")}
                    </button>
                    <button type="button" disabled className="btn-gold cursor-not-allowed opacity-40" data-testid="claim-btn" title={t("mint.claimDisabled")}>
                      {t("mint.claim")}
                    </button>
                  </div>

                  {result && (
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-white/10 bg-ink/60 p-6" data-testid="eligibility-result">
                      <p className="font-heading text-lg font-bold uppercase tracking-wide text-goldlight">
                        {t("mint.state.unavailable")}
                      </p>
                      <ul className="mt-4 space-y-2.5">
                        {(result.checks || []).map((c) => (
                          <li key={c.key} className="flex items-start gap-2.5 text-sm text-mist">
                            {c.passed === true
                              ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-neon" />
                              : <XCircle size={15} className="mt-0.5 shrink-0 text-gold/70" />}
                            {c.detail}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-mist/70">
                        ≥ ${SITE.nftMinHoldingUsd} · {SITE.nftHoldingDays} days
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-6">
          <Link to="/my-nfts" className="btn-ghost" data-testid="goto-mynfts">{t("header.myNfts")}</Link>
        </Reveal>
      </section>

      <section className="section-pad mt-8">
        <Reveal className="card-surface p-7">
          <p className="text-xs leading-relaxed text-mist" data-testid="mint-disclaimer">{t("rewards.disclaimer")}</p>
        </Reveal>
      </section>
    </>
  );
}
